// Sidebar.jsx - Simplified without timeline
import React from 'react';

export default function Sidebar({
  drawerOpen,
  activeView,
  setActiveView,
  addBlock,
  simulationRunning,
  runSimulation,
  pauseSimulation,
  resetSimulation,
  validationIssues = [],
  scenarioStats = {}
}) {
  const viewButtons = [
    {
      id: 'builder',
      label: 'Block Editor',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      description: 'Edit events and choices'
    },
    {
      id: 'flow',
      label: 'Flow Chart',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      description: 'Visual connections view'
    },
    {
      id: 'explorer',
      label: 'Path Explorer',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m-6 3l6-3" />
        </svg>
      ),
      description: 'Test scenario paths interactively'
    },
    {
      id: 'validator',
      label: 'Validator',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      description: 'Check scenario integrity',
      badge: validationIssues.length > 0 ? validationIssues.length : null,
      badgeColor: validationIssues.some(issue => issue.type === 'error') ? 'red' : 'yellow'
    }
  ];

  return (
    <div className={`${drawerOpen ? 'w-72' : 'w-16'} bg-white border-r border-gray-200 transition-all duration-300 overflow-y-auto`}>
      <div className="p-4">
        {drawerOpen && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                {activeView === "builder" && "Builder View"}
                {activeView === "flow" && "Flow View"}
                {activeView === "explorer" && "Path Explorer"}
                {activeView === "validator" && "Validator"}
              </h2>
            </div>
            
            {/* Scenario Overview Stats */}
            {Object.keys(scenarioStats).length > 0 && (
              <div className="mb-6 p-3 bg-gray-50 rounded border border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Scenario Overview</h3>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Total Blocks:</span>
                    <span className="font-medium">{scenarioStats.totalBlocks}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Possible Paths:</span>
                    <span className="font-medium">{scenarioStats.possiblePaths}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Total Duration:</span>
                    <span className="font-medium">{Math.round((scenarioStats.totalDuration || 0) / 60)} min</span>
                  </div>
                  {validationIssues.length > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-red-600">Issues:</span>
                      <span className="font-medium text-red-600">{validationIssues.length}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* View Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Current View</label>
              <div className="space-y-2">
                {viewButtons.map((view) => (
                  <button
                    key={view.id}
                    onClick={() => setActiveView(view.id)}
                    className={`w-full flex items-start gap-3 p-3 rounded text-sm transition-all relative ${
                      activeView === view.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">{view.icon}</div>
                    <div className="text-left flex-1">
                      <div className="font-medium">{view.label}</div>
                      <div className={`text-xs mt-0.5 ${
                        activeView === view.id ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {view.description}
                      </div>
                    </div>
                    {view.badge && (
                      <div className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                        view.badgeColor === 'red' ? 'bg-red-500 text-white' : 'bg-yellow-500 text-white'
                      }`}>
                        {view.badge}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Add Block Section */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Add Blocks</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => addBlock('event')}
                  className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 flex items-center justify-center gap-2 text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Event
                </button>
                <button
                  onClick={() => addBlock('choice')}
                  className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 flex items-center justify-center gap-2 text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Choice
                </button>
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-4">
              <h3 className="font-medium text-sm text-gray-700 mb-2">Simulation Controls</h3>
              <div className="flex gap-2 mb-2">
                {!simulationRunning ? (
                  <button
                    onClick={runSimulation}
                    className="flex-1 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 flex items-center justify-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Simulate
                  </button>
                ) : (
                  <button
                    onClick={pauseSimulation}
                    className="flex-1 bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 flex items-center justify-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Pause
                  </button>
                )}
                <button
                  onClick={resetSimulation}
                  className="flex-1 border border-gray-300 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-50 flex items-center justify-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reset
                </button>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="font-medium text-sm text-gray-700 mb-2">Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setActiveView('validator')}
                  className={`w-full text-sm text-left px-3 py-2 rounded transition-colors ${
                    validationIssues.length > 0 
                      ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100' 
                      : 'bg-green-50 text-green-700 hover:bg-green-100'
                  }`}
                >
                  {validationIssues.length > 0 
                    ? `Fix ${validationIssues.length} Issue${validationIssues.length !== 1 ? 's' : ''}` 
                    : 'Scenario Valid ✓'}
                </button>
                
                <button
                  onClick={() => setActiveView('explorer')}
                  className="w-full text-sm text-left px-3 py-2 rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                >
                  Test Scenario Paths
                </button>
              </div>
            </div>
            
            {/* Help Section */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="font-medium text-sm text-gray-700 mb-2">Quick Guide</h3>
              <div className="text-xs text-gray-600 space-y-1">
                <p><span className="inline-block w-3 h-3 bg-blue-500 rounded mr-2"></span>Event blocks contain actions and injects</p>
                <p><span className="inline-block w-3 h-3 bg-green-500 rounded mr-2"></span>Choice blocks create decision points</p>
                <p><span className="inline-block w-3 h-3 bg-yellow-500 rounded mr-2"></span>Use Validator to check scenario integrity</p>
                <p>• Path Explorer to test specific scenarios</p>
                <p>• Flow Chart for visual editing</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}